import React from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import { Translate, TranslateFunction } from 'react-localize-redux';

import { Box, SkeletonLoader, Typography } from '@fd/ui/atoms';
import LinkButton from '@fd/ui/Button/LinkButton';

import { KeywordChip } from '../KeywordChip';

const useStyles = makeStyles((theme: Theme) => ({
  box: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3.5),
    marginTop: theme.spacing(3),
  },
  container: {
    marginTop: theme.spacing(4),
  },
  editButton: {
    border: `1px solid ${theme.palette.grey[300]}`,
    '&:hover': {
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    height: 'fit-content',
  },
  logo: {
    width: '132px',
    height: '132px',
    borderRadius: '8px',
  },
  subtitle: {
    marginTop: theme.spacing(1),
  },
  title: {
    fontSize: '1.5rem',
  },
  topRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));

export type AppDetailsProps = {
  description?: string;
  isLoading?: boolean;
  keywords?: string[];
  logo?: string;
  shortDescription?: string;
  title?: string;
  translate: TranslateFunction;
};

export const AppDetails = (props: AppDetailsProps) => {
  const { description, isLoading, keywords = [], logo, shortDescription, title, translate } = props;

  const classes = useStyles();

  const renderSkeletonLoader = () => {
    return (
      <Box className={classes.box}>
        <SkeletonLoader
          fdKey="submission-details-loader"
          rows={[
            { height: '132px', width: '132px' },
            { height: '35px', width: '30%', className: classes.container },
            { height: '24px', width: '100%', className: classes.container },
            { height: '24px', width: '50%', className: classes.container },
          ]}
        />
      </Box>
    );
  };
  return isLoading ? (
    renderSkeletonLoader()
  ) : (
    <Box className={classes.box}>
      <div className={classes.topRow}>
        <img
          src={logo}
          className={classes.logo}
          alt={`${title} ${translate('App_logo')}` as string}
        />
        <LinkButton
          className={classes.editButton}
          color="primary"
          fdKey="edit-app-details-button"
          to={`mobile-apps/app-submission`}
          variant="outlined"
        >
          <Translate id="Edit" />
        </LinkButton>
      </div>
      <div className={classes.container}>
        <Typography className={classes.title} variant="h4">
          {title}
        </Typography>
      </div>
      <div className={classes.container}>
        <Typography variant="caption">
          <Translate id="Description" />
        </Typography>
        <div className={classes.subtitle}>
          <Typography variant="subtitle1">{description}</Typography>
        </div>
      </div>
      <div className={classes.container}>
        <Typography variant="caption">
          <Translate id="Short_description" />
        </Typography>
        <div className={classes.subtitle}>
          <Typography variant="subtitle1">{shortDescription}</Typography>
        </div>
      </div>
      <div className={classes.container}>
        <Typography variant="caption">
          <Translate id="Keywords" />
        </Typography>
        <div>
          {keywords.map((keyword: string) => (
            <KeywordChip key={keyword} label={keyword} />
          ))}
        </div>
      </div>
    </Box>
  );
};
